<script setup>
  import { ref, useSlots, computed, onMounted, onUnmounted, watch, defineProps } from 'vue'
  
  const props = defineProps({
    gap: {
      type: String,
      default: '1.5rem'
    },
    slides: {
      type: Number,
      default: null
    }
  })
  
  const slots = useSlots()
  const currentIndex = ref(0)
  const isDragging = ref(false)
  const startX = ref(0)
  const currentX = ref(0)
  const offsetX = ref(0)
  const carouselWidth = ref(0)
  const gapInPixels = ref(0)
  const carouselRef = ref(null)
  
  
  const slideCount = computed(() => {
    if (props.slides !== null && props.slides !== undefined) {
      return props.slides
    }

    const defaultSlot = slots.default?.()
    if (!defaultSlot) return 0
    return defaultSlot.length
  })
  
  const trackStyle = computed(() => {
    if (carouselWidth.value === 0) {
      return { transform: `translateX(0px)` }
    }
    
    const slideWidthPx = carouselWidth.value
    const gapPx = gapInPixels.value
    const slideWithGapPx = slideWidthPx + gapPx
    
    const baseTranslatePx = currentIndex.value * slideWithGapPx
    let translateXPx = -baseTranslatePx
    
    if (isDragging.value) {
      translateXPx = -(baseTranslatePx - offsetX.value)
      
      if (slideCount.value === 1) {
        translateXPx = -baseTranslatePx + offsetX.value * 0.3
      } else {
        const minTranslatePx = 0
        const maxTranslatePx = (slideCount.value - 1) * slideWithGapPx
        const currentTranslatePx = baseTranslatePx - offsetX.value
        
        if (currentTranslatePx < minTranslatePx) {
          translateXPx = -minTranslatePx + (currentTranslatePx - minTranslatePx) * 0.3
        } else if (currentTranslatePx > maxTranslatePx) {
          translateXPx = -maxTranslatePx - (currentTranslatePx - maxTranslatePx) * 0.3
        }
      }
    }
    
    return {
      transform: `translateX(${translateXPx}px)`
    }
  })
  
  const calculateGapInPixels = (gapValue, containerWidth) => {
    if (!gapValue || gapValue === '0') return 0
    
    const num = Number.parseFloat(gapValue)
    if (Number.isNaN(num)) return 0
    
    if (gapValue.includes('rem')) {
      return num * Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
    } else if (gapValue.includes('em')) {
      return num * Number.parseFloat(getComputedStyle(document.body).fontSize)
    } else if (gapValue.includes('%')) {
      return (num / 100) * containerWidth
    } else if (gapValue.includes('px')) {
      return num
    } else {
      return num
    }
  }
  
  const startDrag = (e) => {
    isDragging.value = true
    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
    startX.value = clientX
    currentX.value = clientX
    
    const carouselElement = e.currentTarget
    if (carouselElement) {
      carouselWidth.value = carouselElement.offsetWidth
      gapInPixels.value = calculateGapInPixels(props.gap, carouselWidth.value)
    }
  }
  
  const onDrag = (e) => {
    if (!isDragging.value) return
    
    e.preventDefault()
    currentX.value = e.type.includes('touch') ? e.touches[0].clientX : e.clientX
    const rawOffsetX = currentX.value - startX.value

    if (currentIndex.value === 0 && rawOffsetX > 0) {
      offsetX.value = 0
    } else if (currentIndex.value === slideCount.value - 1 && rawOffsetX < 0) {
      offsetX.value = 0
    } else {
      offsetX.value = rawOffsetX
    }
  }
  
  const endDrag = () => {
    if (!isDragging.value) return
    
    if (slideCount.value <= 1) {
      isDragging.value = false
      offsetX.value = 0
      return
    }
    
    const threshold = 50 
    
    if (Math.abs(offsetX.value) > threshold) {
      if (offsetX.value > 0 && currentIndex.value > 0) {
        currentIndex.value--
      } 
      else if (offsetX.value < 0 && currentIndex.value < slideCount.value - 1) {
        currentIndex.value++
      }
    }
    
    isDragging.value = false
    offsetX.value = 0
  }
  
  const goToSlide = (index) => {
    currentIndex.value = index
  }
  
  const nextSlide = () => {
    if (currentIndex.value < slideCount.value - 1) {
      currentIndex.value++
    }
  }
  
  const prevSlide = () => {
    if (currentIndex.value > 0) {
      currentIndex.value--
    }
  }
  
  const canGoNext = computed(() => currentIndex.value < slideCount.value - 1)
  const canGoPrev = computed(() => currentIndex.value > 0)
  
  const updateGap = () => {
    if (carouselRef.value) {
      carouselWidth.value = carouselRef.value.offsetWidth
      gapInPixels.value = calculateGapInPixels(props.gap, carouselWidth.value)
    }
  }
  
  onMounted(() => {
    updateGap()
    window.addEventListener('resize', updateGap)
  })
  
  onUnmounted(() => {
    window.removeEventListener('resize', updateGap)
  })
  
  watch(() => props.gap, () => {
    updateGap()
  })
  </script>
<template>
  <div class="carousel-container">
    <div class="carousel-wrapper">
      <button
        v-if="slideCount > 1"
        class="carousel-arrow carousel-arrow-prev"
        :class="{ disabled: !canGoPrev }"
        @click="prevSlide"
        :disabled="!canGoPrev"
        aria-label="Slide anterior"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      <div 
        ref="carouselRef"
        class="carousel"
        @mousedown="startDrag"
        @touchstart="startDrag"
        @mousemove="onDrag"
        @touchmove="onDrag"
        @mouseup="endDrag"
        @mouseleave="endDrag"
        @touchend="endDrag"
        :style="{ '--carousel-gap': props.gap, '--carousel-gap-half': `calc(${props.gap} / 2)` }"
      >
        <div 
          class="carousel-track"
          :class="{ dragging: isDragging }"
          :style="{ ...trackStyle, gap: props.gap }"
        >
          <slot />
        </div>
      </div>
      
      <button
        v-if="slideCount > 1"
        class="carousel-arrow carousel-arrow-next"
        :class="{ disabled: !canGoNext }"
        @click="nextSlide"
        :disabled="!canGoNext"
        aria-label="Próximo slide"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    
    <div class="indicators">
      <button
        v-for="(_, index) in slideCount"
        :key="index"
        class="indicator"
        :class="{ active: currentIndex === index }"
        @click="goToSlide(index)"
        :aria-label="`Ir para slide ${index + 1}`"
      >
      </button>
    </div>
  </div>
</template>



<style scoped>
.carousel-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.carousel-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.carousel {
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: grab;
  user-select: none;
  min-width: 0;
}

.carousel-arrow {
  flex-shrink: 0;
  width: 32x;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: rgba(255, 255, 255, 0.7);
  padding: 0;
  background: #0F0F0F;
  border: 1px solid #232323;
  border-radius: 5px;
}

.carousel-arrow:hover:not(.disabled) {
  color: rgba(255, 255, 255, 1);
  background: #232323;
  border-color: #393939;

}

.carousel-arrow.disabled {
  opacity: 0.3;
  cursor: not-allowed;
  pointer-events: none;
}

@media (max-width: 768px) {
  .carousel-wrapper {
    gap: 0.5rem;
  }
  
  .carousel-arrow {
    width: 36px;
    height: 36px;
  }
  
  .carousel-arrow svg {
    width: 18px;
    height: 18px;
  }
}


.carousel:active {
  cursor: grabbing;
}

.carousel-track {
  display: flex;
  height: 100%;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.carousel-track.dragging {
  transition: none;
}

.carousel-track > * {
  height: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 100%;
  min-width: 100%;
}

.indicators {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding-top: 1rem;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.5);
  
}

.indicator.active {
  background: rgba(255, 255, 255, 1);
  width: 24px;
  border-radius: 4px;
}
</style>
